import { TFunction } from 'i18next';
import flatMap from 'lodash/flatMap';

import {
  CatalogEntry,
  CourseSubformat,
  CourseType,
  DeliveryFormat,
  ExamType,
  Modality,
  OfferingStatus,
  OfferingVisibility,
  VocabularyNamespaces,
} from 'types';
import {
  prioritizeEntryField,
  FilterState,
  FilterConfiguration,
} from 'stores/common/Filters';

interface IsCompReview {
  (
    offeringModality: Modality,
    offeringType: CourseType,
    subformat: CourseSubformat,
  ): boolean;
}

// These fields are used to extract the categories and products
// from the catalog entries. The first element is prioritized over the second,
// and so on.
export const TAXONOMIES_PRIORITIES = {
  topics: ['metadata.taxonomies.topics', 'taxonomies.topics'],
  audiences: ['metadata.taxonomies.audiences', 'taxonomies.audiences'],
  products: ['metadata.taxonomies.products', 'taxonomies.products'],
  level: ['metadata.level', 'level'], // the only one outside of taxonomies
};

export const compareByLevelAsc = (a: FilterState, b: FilterState) =>
  parseInt(a.key, 10) - parseInt(b.key, 10);

export const buildGetDeliveryFormat =
  (isCompReview: IsCompReview) =>
  (obj: CatalogEntry): DeliveryFormat | undefined => {
    if (obj.course_type && obj.course_type !== CourseType.Regular) {
      // consider labplus as a lesson in this filter:
      if (obj.course_type === CourseType.LabPlus) {
        return CourseType.Lesson;
      }
      if (isCompReview(obj.modality, obj.course_type, obj.subformat)) {
        return CourseSubformat.CompReview;
      }
      // lessons, labplus, etc
      return obj.course_type ? obj.course_type : obj.modality;
    }

    if (obj.status === OfferingStatus.EarlyAccess) {
      return OfferingStatus.EarlyAccess;
    }

    if (obj?.exam_type === ExamType.Experience) {
      return ExamType.Experience;
    }
    return obj.modality;
  };

/**
 * Returns the filter configurations for the catalog.
 * @returns The filter configurations.
 */
export const getFilterConfigurations = (
  // eslint-disable-next-line @typescript-eslint/default-param-last
  showMyOfferingsFilter: boolean = false,
  isCompReview: IsCompReview,
  t: TFunction = (str: string) => str,
): FilterConfiguration<CatalogEntry>[] => {
  const dfFilterConfig: FilterConfiguration<CatalogEntry> = {
    key: 'deliveryFormats',
    title: t('Delivery formats'),
    extractFilterValues: (entries) => {
      const getDeliveryFormat = buildGetDeliveryFormat(isCompReview);
      return flatMap(entries, getDeliveryFormat);
    },
    vocabularyNamespace: VocabularyNamespaces.Modalities,
    compareFn: (a: FilterState, b: FilterState) => {
      // custom order for modality
      const customOrder = [
        CourseType.Lesson,
        Modality.Course,
        Modality.Exam,
        Modality.TechOverview,
        CourseSubformat.CompReview,
        CourseType.LabPlus,
        ExamType.Experience,
        OfferingStatus.EarlyAccess,
        Modality.ExpertSeminar,
      ];

      const indexOfA = customOrder.indexOf(a.key as CourseType | Modality);
      const indexOfB = customOrder.indexOf(b.key as CourseType | Modality);

      if (indexOfA === -1) {
        return 1;
      }

      return indexOfA - indexOfB;
    },
    validateEntry: (entry: CatalogEntry, checkedDeliveryFormats) => {
      let entryModality = '';
      let entryStatus = '';

      if (
        (entry?.course_type === CourseType.Regular &&
          entry.status !== OfferingStatus.EarlyAccess) ||
        (entry?.modality && !entry?.course_type)
      ) {
        entryModality = entry?.modality;
      }

      if (entry.status === OfferingStatus.EarlyAccess) {
        entryStatus = entry?.status;
      }

      let entryCourseType = (entry?.course_type as string) || '';

      if (entry?.course_type === CourseType.LabPlus) {
        entryCourseType = CourseType.Lesson;
      }

      if (isCompReview(entry.modality, entry.course_type, entry.subformat)) {
        entryCourseType = CourseSubformat.CompReview;
      }

      if (entry?.exam_type === ExamType.Experience) {
        entryModality = ExamType.Experience;
      }

      return (
        checkedDeliveryFormats.includes(entryModality) ||
        checkedDeliveryFormats.includes(entryCourseType) ||
        checkedDeliveryFormats.includes(entryStatus)
      );
    },
  };

  const rolesFilterConfig: FilterConfiguration<CatalogEntry> = {
    key: 'roles',
    title: t('Job roles'),
    extractFilterValues: (entries) => flatMap(entries, 'job_roles'),
    vocabularyNamespace: VocabularyNamespaces.JobRoles,
    validateEntry: (entry: CatalogEntry, checkedRoles) => {
      const { job_roles: jobRoles } = entry;
      const filtered = jobRoles?.filter((item) => checkedRoles.includes(item));
      return jobRoles?.some((item) => filtered.includes(item));
    },
  };

  const myOfferingsConfig: FilterConfiguration<CatalogEntry> = {
    key: 'myOfferings',
    extractFilterValues: (entries) =>
      entries.reduce((filteredEntries, entry) => {
        const shouldDisplay = entry?.visibility === OfferingVisibility.Visible;

        if (shouldDisplay) {
          return [...filteredEntries, entry.visibility];
        }

        return filteredEntries;
      }, []),
    vocabulary: [
      {
        display_name: t('Available to me'),
        token: OfferingVisibility.Visible,
      },
    ],
    validateEntry: (entry, checkedRHLSTrial) =>
      checkedRHLSTrial.includes(entry?.visibility),
  };

  const filterConfigurations: FilterConfiguration<CatalogEntry>[] = [
    ...(showMyOfferingsFilter ? [myOfferingsConfig] : []),
    dfFilterConfig,
    rolesFilterConfig,
    {
      key: 'taxonomyProducts',
      title: t('Products'),
      extractFilterValues: (entries) =>
        entries
          .map((entry) =>
            prioritizeEntryField<CatalogEntry>(
              entry,
              TAXONOMIES_PRIORITIES.products,
              true,
            ),
          )
          .flat(),
      vocabularyNamespace: VocabularyNamespaces.OfferingTaxonomyProducts,
      validateEntry: (entry, checkedTaxonomyProducts) => {
        const entryProducts: string[] = prioritizeEntryField<CatalogEntry>(
          entry,
          TAXONOMIES_PRIORITIES.products,
          true,
        );

        const filtered =
          entryProducts?.filter((item) =>
            checkedTaxonomyProducts.includes(item),
          ) || [];

        return entryProducts?.some((item) => filtered.includes(item));
      },
    },
    {
      key: 'taxonomyTopics',
      title: t('Topics'),
      extractFilterValues: (entries) =>
        entries
          .map((entry) =>
            prioritizeEntryField<CatalogEntry>(
              entry,
              TAXONOMIES_PRIORITIES.topics,
              true,
            ),
          )
          .flat(),
      vocabularyNamespace: VocabularyNamespaces.OfferingTaxonomyTopics,
      validateEntry: (entry, checkedTopics) => {
        const entryTopics: string[] = prioritizeEntryField<CatalogEntry>(
          entry,
          TAXONOMIES_PRIORITIES.topics,
          true,
        );
        const filtered = entryTopics?.filter((item) =>
          checkedTopics.includes(item),
        );

        return entryTopics?.some((item) => filtered.includes(item));
      },
    },
    {
      key: 'taxonomyAudiences',
      title: t('Audiences'),
      extractFilterValues: (entries) =>
        entries
          .map((entry) =>
            prioritizeEntryField<CatalogEntry>(
              entry,
              TAXONOMIES_PRIORITIES.audiences,
              true,
            ),
          )
          .flat(),
      vocabularyNamespace: VocabularyNamespaces.OfferingTaxonomyAudiences,
      validateEntry: (entry, checkedAudiences) => {
        const entryAudiences: string[] = prioritizeEntryField<CatalogEntry>(
          entry,
          TAXONOMIES_PRIORITIES.audiences,
          true,
        );

        const filtered = entryAudiences?.filter((item) =>
          checkedAudiences.includes(item),
        );

        return entryAudiences?.some((item) => filtered.includes(item));
      },
    },
    {
      key: 'level',
      title: t('Levels'),
      groupIdenticalLabels: true,
      extractFilterValues: (entries) =>
        entries
          .map((entry) =>
            prioritizeEntryField<CatalogEntry>(
              entry,
              TAXONOMIES_PRIORITIES.level,
              false,
            ),
          )
          .flat(),
      vocabularyNamespace: VocabularyNamespaces.OfferingLevel,
      compareFn: compareByLevelAsc,
      validateEntry: (entry, checkedLevels) => {
        const entryLevel = prioritizeEntryField<CatalogEntry>(
          entry,
          TAXONOMIES_PRIORITIES.level,
          false,
        );

        return checkedLevels.includes(`${entryLevel}`);
      },
    },
  ];

  filterConfigurations.push({
    key: 'languages',
    title: t('Languages'),
    extractFilterValues: (entries) =>
      flatMap(entries, (key) =>
        key.translations?.map((translation) => translation?.language),
      ),
    vocabularyNamespace: VocabularyNamespaces.Languages,
    validateEntry: (entry: CatalogEntry, checkedLanguages) => {
      const languageMap = flatMap(entry.translations, 'language');
      const filtered = languageMap?.filter((item) =>
        checkedLanguages.includes(item),
      );
      return languageMap?.some((item) => filtered.includes(item));
    },
  });

  return filterConfigurations;
};
